import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import PasswordRequirementsHint from '@/components/PasswordRequirementsHint'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

function isEmailNotConfirmedError(error: { message?: string } | null): boolean {
  const msg = (error?.message ?? '').toLowerCase()
  return msg.includes('email') && (msg.includes('not confirmed') || msg.includes('confirm'))
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect')
  const emailFromUrl = searchParams.get('email') ?? ''
  const inviteTokenParam = searchParams.get('invite_token') ?? ''
  const tokenFromRedirect =
    redirectTo?.includes('/invite/accept') && redirectTo.includes('token=')
      ? (() => {
          const match = redirectTo.match(/[?&]token=([^&]+)/)
          return match ? decodeURIComponent(match[1]) : ''
        })()
      : ''
  const inviteToken =
    inviteTokenParam ||
    tokenFromRedirect ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('invite_confirm_token') : null) ||
    ''
  if ((inviteTokenParam || tokenFromRedirect) && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('invite_confirm_token', inviteToken)
  }
  const [email, setEmail] = useState(emailFromUrl)
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [showPasswordRules, setShowPasswordRules] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (emailFromUrl) setEmail(emailFromUrl)
  }, [emailFromUrl])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    let signInResult = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    const isUnconfirmed = signInResult.error && isEmailNotConfirmedError(signInResult.error)

    if (signInResult.error && (isUnconfirmed || inviteToken)) {
      let result: { confirmed?: boolean; error?: string; message?: string } = {}
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (SUPABASE_ANON_KEY) {
        headers.apikey = SUPABASE_ANON_KEY
        headers.Authorization = `Bearer ${SUPABASE_ANON_KEY}`
      }
      if (inviteToken) {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/confirm-email-invite`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ email, token: inviteToken }),
        })
        result = (await res.json().catch(() => ({}))) as typeof result
      }
      if (!result.confirmed && !inviteToken && isUnconfirmed) {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/confirm-email-bypass`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ email }),
        })
        result = (await res.json().catch(() => ({}))) as typeof result
      }
      if (result.confirmed) {
        signInResult = await supabase.auth.signInWithPassword({ email, password })
      }
      if (!result.confirmed && (inviteToken || isUnconfirmed)) {
        setError(
          result.message ??
            result.error ??
            (inviteToken
              ? 'Email could not be confirmed for this invite. Please use the verification link we sent, or try again.'
              : 'Email not confirmed. Use the verification link we sent to your email, or ask an admin to enable "Bypass email verification" in Admin → Feature flags.')
        )
        setLoading(false)
        return
      }
    }

    if (signInResult.error) {
      setError(signInResult.error.message)
      setLoading(false)
    } else {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('invite_confirm_token')
      navigate(redirectTo && redirectTo.startsWith('/') ? redirectTo : '/', { replace: true })
    }
  }

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6">Login</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block mb-2 font-semibold">
            Email
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-required="true"
            className="w-full"
            autoComplete="email"
          />
        </div>

        <div>
          <label htmlFor="password" className="block mb-2 font-semibold">
            Password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <div className="flex gap-2">
            <input
              type={passwordVisible ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
              className="w-full flex-1"
              autoComplete="current-password"
              aria-describedby={showPasswordRules ? 'login-password-rules' : undefined}
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              className="px-3 py-2 rounded border shrink-0"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            >
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="mt-2 text-base text-text-primary">
            New user? <Link to="/register" className="text-text-link underline font-semibold">Register</Link>
            {' · '}
            <Link to="/forgot-password" className="text-text-link underline font-semibold">Forgot password?</Link>
            {' · '}
            <button
              type="button"
              onClick={() => setShowPasswordRules((v) => !v)}
              className="text-text-link underline font-semibold bg-transparent border-0 p-0 cursor-pointer"
              aria-expanded={showPasswordRules}
              aria-controls="login-password-rules"
            >
              {showPasswordRules ? 'Hide password requirements' : 'Show password requirements'}
            </button>
          </p>
          {showPasswordRules && (
            <div className="mt-2">
              <PasswordRequirementsHint id="login-password-rules" showLabel={true} />
            </div>
          )}
        </div>

        {error && (
          <div role="alert" className="text-color-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-bg-interactive text-text-inverse hover:bg-bg-interactive-hover disabled:opacity-50"
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    </div>
  )
}

